// WHAT CAME IN THIS MONTH, AND TO WHOM. The first thing on the screen, and the smallest
// question it asks: for one month, how much money arrived in total, how much of it each
// partner banked, and the spread between them.
//
// THIS IS NOT THE GAP, AND THE CARD IS BUILT TO KEEP IT THAT WAY. The standing gap
// between the partners is cumulative — entitlement is a share of profit over all time
// and balances to zero only over all time — so there is no monthly value of it and this
// card never implies one. Concretely:
//   - the word לטובת never appears here. That phrasing belongs to the gap, and a
//     month's spread is not a claim about what anyone is owed: it says which business
//     issued the invoices that happened to be paid this month, nothing more.
//   - the note under the tiles points down to the cumulative card by name, so the two
//     are related on screen rather than left to be conflated.
//   - the arrows change local state only. Nothing here re-fetches the balance, so the
//     gap below cannot move when the month does — see Settlements.js.
//
// EVERY FIGURE IS THE SERVER'S. `byMonth` is `monthlyCashflow`'s own per-partner months,
// transposed by calculations/monthlyCollection and asserted to pivot back to the
// cumulative totals the gap agrees with. The client picks a row out of it and renders;
// it sums nothing and subtracts nothing.
import he from '../../i18n/he';
import MonthStepper from '../../components/MonthStepper';
import { formatMoney } from '../../components/Money';

const t = he.settlements.monthlyCollection;

// A month with no money in it is a real answer, not a missing one — the arrows are
// open-ended, so any month is reachable and a quiet one reads as zeros. Built from the
// partner list the balance already carries, so the tiles keep their identity and their
// order whether or not that month has rows.
const emptyMonth = (month, partners) => ({
  month,
  total: 0,
  partners: partners.map((p) => ({ partnerId: p.partnerId, name: p.name, collected: 0 })),
  difference: null,
});

const Figure = ({ label, value, sub = null, tone = '' }) => (
  <div className="settle-figure">
    <span className="settle-figure-label">{label}</span>
    <span className={`settle-figure-value num ${tone}`}>{value}</span>
    {sub && <span className="settle-figure-sub">{sub}</span>}
  </div>
);

export default function MonthlyCollection({ byMonth, partners, month, onMonthChange }) {
  const row = byMonth.find((m) => m.month === month) || emptyMonth(month, partners);

  return (
    <div className="card settle-card">
      <div className="row settle-month-head">
        <h2>{t.title}</h2>
        <div className="period-picker">
          <MonthStepper
            month={month}
            onChange={onMonthChange}
            prevLabel={t.prevMonth}
            nextLabel={t.nextMonth}
          />
        </div>
      </div>

      {/* Plain figures, not tiles: the card is already the surface, and a card of cards
          is four boxes saying one thing. */}
      <div className="settle-figures">
        <Figure label={t.total} value={formatMoney(row.total)} tone="pos" />
        {row.partners.map((p) => (
          <Figure key={p.partnerId} label={p.name} value={formatMoney(p.collected)} />
        ))}
        {/* A level month prints "מאוזן" rather than ₪0 — a signed zero reads as a figure. */}
        <Figure
          label={t.difference}
          value={row.difference ? formatMoney(row.difference.amount) : t.level}
          sub={row.difference ? `${t.higherAt} ${row.difference.higherName}` : null}
        />
      </div>
    </div>
  );
}
